import { Component, ChangeDetectionStrategy, Signal } from '@angular/core';
import { Params } from '@angular/router';
import { AkitaLocationQuery } from '@app/akita/api/location/state/location.query';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { logSelectPromotion } from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { combineLatest, map, Observable } from 'rxjs';
import { AsyncPipe, CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { AkitaRemoteConfigQuery } from '@app/akita/api/remote-config/state/remote-config.query';
import { toSignal } from '@angular/core/rxjs-interop';
import { CarouselBannerSlide } from '@app/akita/api/remote-config/models/carousel-banner-slide.model';

import { SwiperModule } from 'swiper/angular';
import {
  Swiper,
  Navigation,
  Pagination,
  FreeMode,
  A11y,
  Controller,
  Thumbs,
  HashNavigation,
  Autoplay,
} from 'swiper';

Swiper.use([
  Navigation,
  Pagination,
  FreeMode,
  A11y,
  Controller,
  Thumbs,
  HashNavigation,
  Autoplay,
]);

const FIREBASE_BANNER_FOLDER =
  'https://firebasestorage.googleapis.com/v0/b/popsygae.appspot.com/o/CarouselBanner%2F';
const FIREBASE_BANNER_FOLDER_2 =
  '.png?alt=media&token=98408d1d-460e-4027-a750-d9a744708669';

const ANDROID_DOWNLOAD_LINK =
  'https://play.google.com/store/apps/details?id=com.mypopsy.android';
const IOS_DOWNLOAD_LINK =
  'https://apps.apple.com/app/popsy-shop-online-deals/id1387389249?l=en-GB';

@Component({
  selector: 'app-carousel-banner',
  templateUrl: './carousel-banner.template.html',
  styleUrls: ['./carousel-banner.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, CommonModule, AsyncPipe, SwiperModule],
})
export class CarouselBannerComponent {
  public readonly isBrowser: boolean;
  public readonly queryParamsBanner$: Observable<Params>;
  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public readonly isRtlLayout$: Observable<boolean>;

  public thumbsSwiper: any;

  public bannerSlideLinks: Signal<Array<CarouselBannerSlide> | null>;

  public appDownloadLink = ANDROID_DOWNLOAD_LINK;

  public urlSearchFragments: string | string[];
  public urlPhoneBrandsFragment: string | string[];

  public country$: Observable<string>;

  constructor(
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly analyticsService: GoogleAnalyticsService,
    private readonly akitaLocationQuery: AkitaLocationQuery,
    private readonly akitaRemoteConfigQuery: AkitaRemoteConfigQuery,
    private readonly transloco: TranslocoService
  ) {
    this.urlSearchFragments = this.searchUrlFragment;
    this.urlPhoneBrandsFragment = this.phoneBrandsUrlFragment;

    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.bannerSlideLinks = toSignal(
      combineLatest([
        this.akitaRemoteConfigQuery.selectCarouselBannerSlides(),
        this.akitaScreenQuery.selectBiggerThan(),

        this.akitaRouterQuery.selectCountry(),
      ]).pipe(
        map(([slides, biggerThan, country]) => {
          if (slides?.length) {
            console.log('country', country);
            return slides
              ?.filter(
                // eslint-disable-next-line @typescript-eslint/naming-convention
                (slide) =>
                  !(
                    slide.folder === 'Installments' &&
                    country !== 'SA' &&
                    country !== 'AE'
                  )
              )
              .map((slide) => {
                const lang = this.transloco.getActiveLang().toLowerCase();
                let size = biggerThan.sm
                  ? 'large'
                  : biggerThan.mobileXL
                    ? 'big'
                    : biggerThan.mobileL
                      ? 'medium'
                      : 'small';
                if (
                  slide.folder === 'Installments' &&
                  (country === 'SA' || country === 'AE')
                ) {
                  size = size + '_' + country.toLocaleLowerCase();
                }
                return {
                  ...slide,
                  folder: `${FIREBASE_BANNER_FOLDER}${slide.folder}%2F${lang}%2F${size}${FIREBASE_BANNER_FOLDER_2}`,
                };
              });
          } else {
            return null;
          }
        })
      ),
      { initialValue: null }
    );

    this.queryParamsBanner$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.country$ = this.akitaLocationQuery
      .selectCountry()
      .pipe(map((country: string) => `${country || 'us'}`.toUpperCase()));

    this.appDownloadLink =
      this.akitaScreenQuery.os === 'iOS' ? IOS_DOWNLOAD_LINK : ANDROID_DOWNLOAD_LINK;
  }

  private get searchUrlFragment(): Array<string> {
    return this.akitaRouterQuery.getPhoneSearchFragment();
  }

  private get phoneBrandsUrlFragment(): Array<string> {
    const fragment = this.akitaRouterQuery.homeUrlFragment;
    fragment.push('products');
    return fragment;
  }

  public reviewsUrlFragment(): Array<string> {
    const fragment = this.akitaRouterQuery.homeUrlFragment;
    fragment.push('customer-reviews');
    return fragment;
  }

  public onSelectPromotion(): boolean {
    logSelectPromotion(
      this.analyticsService.logEventWrapper,
      null,
      'featured_banner_actuib',
      'featured_banner_title',
      'top_banner',
      'top_banner'
    );
    return true;
  }

  public scrollToReviews($event: Event): void {
    try {
      if (this.akitaRouterQuery.isBrowser && window.document) {
        $event.stopPropagation();
        $event.preventDefault();
        document.getElementById('reviewsBanner')?.scrollIntoView();
      }
    } catch (err) {
      /* SSR Not Supported */
    }
  }

  public setBgGradiant(color: string): any {
    return { background: ` radial-gradient(#fff,${color}` };
  }

  public range(length: number | undefined): Array<number> {
    return Array(length);
  }
  public trackByIndex(index: number, value?: CarouselBannerSlide | null): string {
    return `${value?.urlLink || index}`;
  }
}
